import { useCallback, useEffect, useState } from 'react';
import {
  EXPORTER_COST_MAPPINGS_CHANGED_EVENT,
  ExporterCostCategory,
  getCustomCostCategoriesEnabled,
  getExporterCostCategories,
  getReceiverCostExporterMappings,
} from 'common/src/api/exporter-cost-categories';

/**
 * Exporter categories and the receiver's cost-name mappings, for showing a
 * settlement's costs in the Estándar Exportadora while it is being uploaded or
 * edited. Reloads when mappings are saved anywhere in the app.
 */
export const useReceiverExporterCosts = (
  exporterId: number | null | undefined,
  exporterReceiverId: number | null | undefined
) => {
  const [enabled, setEnabled] = useState(false);
  const [categories, setCategories] = useState<ExporterCostCategory[]>([]);
  const [mappings, setMappings] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    if (!exporterId) {
      setEnabled(false);
      return;
    }
    try {
      const isEnabled = await getCustomCostCategoriesEnabled(exporterId);
      setEnabled(isEnabled);
      if (!isEnabled) return;
      const [nextCategories, nextMappings] = await Promise.all([
        getExporterCostCategories(exporterId, true),
        exporterReceiverId
          ? getReceiverCostExporterMappings(exporterId, exporterReceiverId)
          : Promise.resolve({}),
      ]);
      setCategories(nextCategories);
      setMappings(nextMappings);
    } catch (error) {
      console.error('Error loading exporter cost categories:', error);
    }
  }, [exporterId, exporterReceiverId]);

  useEffect(() => {
    load();
    window.addEventListener(EXPORTER_COST_MAPPINGS_CHANGED_EVENT, load);
    return () =>
      window.removeEventListener(EXPORTER_COST_MAPPINGS_CHANGED_EVENT, load);
  }, [load]);

  return {
    available: enabled && categories.length > 0,
    categories,
    mappings,
  };
};
